/**
 * What an upload link will accept (COMMUNICATIONS-DESIGN C7).
 *
 * ⚠️ **One definition, read by four places.** The producer's picker, the
 * `accept` attribute on the public file input, the sentence the public page
 * shows, and the server's refusal all need the same answer to "is a .docx
 * allowed here". Written four times they drift, and the way they drift is
 * silent: a page that accepts a file the server then rejects after the upload
 * has already been paid for.
 *
 * ⚠️ **The browser's `accept` is a convenience, never the control.** It filters
 * a file dialog and is trivially bypassed — the input can be handed a file by
 * drag-and-drop, and the endpoints can be called without a browser at all. So
 * the server checks the same list on both the presign and the finalize, and
 * this module exists to make those checks agree with what the guest was shown
 * rather than to replace them.
 */
export const UPLOAD_FILE_TYPES = [
    {
        key: 'PDF',
        label: 'PDF',
        mimeTypes: ['application/pdf'],
        extensions: ['.pdf'],
    },
    {
        key: 'IMAGE',
        label: 'Images',
        mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'],
        extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'],
    },
    {
        key: 'DOCUMENT',
        label: 'Documents',
        mimeTypes: [
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain',
            'application/rtf',
        ],
        extensions: ['.doc', '.docx', '.txt', '.rtf'],
    },
    {
        key: 'SPREADSHEET',
        label: 'Spreadsheets',
        mimeTypes: [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv',
        ],
        extensions: ['.xls', '.xlsx', '.csv'],
    },
];
export function uploadFileType(key) {
    return UPLOAD_FILE_TYPES.find((type) => type.key === key.toUpperCase());
}
/** The `accept` attribute for a set of keys, or '' meaning "anything". */
export function uploadAccept(keys) {
    const types = (keys ?? [])
        .map(uploadFileType)
        .filter((type) => type !== undefined);
    if (types.length === 0)
        return '';
    return [...new Set(types.flatMap((type) => [...type.mimeTypes, ...type.extensions]))].join(',');
}
/** How to describe the restriction to somebody about to choose a file. */
export function uploadAcceptLabel(keys) {
    const labels = (keys ?? [])
        .map(uploadFileType)
        .filter((type) => type !== undefined)
        .map((type) => type.label);
    if (labels.length === 0)
        return null;
    if (labels.length === 1)
        return labels[0];
    return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}
/**
 * Whether a file may be uploaded.
 *
 * ⚠️ **An empty list means "anything", and that is the existing behaviour.**
 * Every upload link created before this feature has no restriction recorded,
 * and must keep working exactly as it did; a list that defaulted to "nothing"
 * would silently break every link already in somebody's inbox.
 *
 * ⚠️ **A missing list means the same, rather than throwing.** This is called
 * from inside `finalizeUpload`, after the guest has already uploaded — so a
 * row that predates the column, or any caller passing nothing, must read as
 * "unrestricted" instead of losing a file that has been sent.
 */
export function isUploadFileAllowed(keys, mimeType, fileName) {
    const types = (keys ?? [])
        .map(uploadFileType)
        .filter((type) => type !== undefined);
    if (types.length === 0)
        return true;
    const mime = (mimeType ?? '').trim().toLowerCase();
    const name = (fileName ?? '').trim().toLowerCase();
    return types.some((type) => (mime !== '' && type.mimeTypes.some((allowed) => allowed.toLowerCase() === mime)) ||
        (name !== '' && type.extensions.some((ext) => name.endsWith(ext))));
}
/** The type each registry extension is stored as when the browser reports none. */
const EXTENSION_MIME_TYPES = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.txt': 'text/plain',
    '.rtf': 'application/rtf',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
};
/**
 * Other names browsers give an extension's own type. A `.csv` in particular
 * arrives as `text/plain`, `text/x-csv` or `application/csv` depending on the
 * browser and the operating system. These are stored as the canonical type,
 * never as reported, so an alias can only ever widen what is accepted to that
 * one inert type.
 */
const EXTENSION_MIME_ALIASES = {
    '.csv': [
        'text/plain',
        'text/x-csv',
        'text/comma-separated-values',
        'application/csv',
        'application/x-csv',
        'application/vnd.ms-excel',
    ],
    '.jpg': ['image/pjpeg'],
    '.jpeg': ['image/pjpeg'],
};
/**
 * The content type a file may be stored with, or null when it may not be
 * uploaded at all. Stricter than `isUploadFileAllowed`, and meant for uploads
 * the office will open.
 *
 * ⚠️ `isUploadFileAllowed` accepts a permitted extension **or** a permitted
 * type, which is right for its job of matching what a guest was told. But the
 * type is what S3 serves the file with. A `quote.pdf` declared as `text/html`
 * would pass that check and then open as a live page for whoever downloads
 * it. So here:
 * - the extension must be one of the allowed types';
 * - the reported type must be one that same type allows;
 * - a blank or generic `application/octet-stream` report (browsers send one
 *   for some `.csv` and `.msg` files), or a known alias of the extension's own
 *   type (`EXTENSION_MIME_ALIASES`), takes the extension's canonical type.
 * An empty list allows nothing, unlike `isUploadFileAllowed`.
 */
export function strictUploadMimeType(keys, fileName, reportedMimeType) {
    const name = fileName.trim().toLowerCase();
    const dot = name.lastIndexOf('.');
    if (dot < 0)
        return null;
    const extension = name.slice(dot);
    const type = keys
        .map(uploadFileType)
        .find((t) => t !== undefined && t.extensions.includes(extension));
    if (!type)
        return null;
    const reported = (reportedMimeType ?? '').trim().toLowerCase();
    if (reported === '' ||
        reported === 'application/octet-stream' ||
        (EXTENSION_MIME_ALIASES[extension] ?? []).includes(reported)) {
        return EXTENSION_MIME_TYPES[extension] ?? null;
    }
    return type.mimeTypes.some((allowed) => allowed.toLowerCase() === reported) ? reported : null;
}
//# sourceMappingURL=upload-file-types.js.map